import {
    Box,
    FormControlLabel,
    InputLabel,
    Stack,
    Switch,
    Tooltip,
} from '@mui/material';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import {
    RequestStatus,
    useServerErrorEffect,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';
import { useState } from 'react';
import {
    ConfirmationDialog,
    ConfirmationDialogVersions,
} from '../operator-logins/components/ConfirmationDialog.tsx';
import {
    PeripheryEventInfos,
    PeripheryInfo,
    usePeripheryDetailsOutlet,
} from './CloudConnectorPeripheriesDetail.tsx';
import { Typo } from 'dg-web-shared/ui/typo.ts';
import {
    CloudConnectorCommandType,
    isPeripheryDisconnected,
    RequestErrorAlert,
    usePeripheryCommand,
} from './CloudConnectorPeripheryDetailTabs.tsx';
import {
    CloudConnectorPeripheryEventType,
    CloudConnectorPeripheryType,
} from 'dg-web-shared/model/CloudConnector.ts';
import InfoIcon from '@mui/icons-material/Info';

interface SetEntryManualCloseRequest {
    operatorId: number;
    zoneId: number;
    onPremiseId: number;
    manualClose: boolean;
}

interface BarrierOpensWithoutValidationRequest {
    operatorId: number;
    zoneId: number;
    onPremiseId: number;
    openWithoutValidation: boolean;
}

export function PeripheryDetailTab() {
    const { currentLogin } = useOperatorContext();
    const { peripheryConfig, refetchCloudConnectorPeriphery } =
        usePeripheryDetailsOutlet();
    const isDisconnected = isPeripheryDisconnected(peripheryConfig);
    const [peripheryCommandRequestState, executePeripheryCommand] =
        usePeripheryCommand();
    const [openPermanentlyOpenModal, setOpenPermanentlyOpenModal] =
        useState(false);
    const barrierPermanentlyOpen = peripheryConfig.events.some(
        event =>
            event.eventType ===
            CloudConnectorPeripheryEventType.BARRIER_PERMANENTLY_OPEN,
    );
    useServerErrorEffect(peripheryCommandRequestState, () =>
        setOpenPermanentlyOpenModal(false),
    );
    useServerSuccessEffect(peripheryCommandRequestState, () =>
        setOpenPermanentlyOpenModal(false),
    );

    const [setEntryManualCloseRequestState, setEntryManualCloseRequest] =
        useServerWrite<SetEntryManualCloseRequest, null>(
            ({ operatorId, zoneId, onPremiseId }) => ({
                url: `/ui-api/operator-account/${operatorId}/zone-barrier-gate/${zoneId}/set-entry-manual-close/${onPremiseId}`,
            }),
        );
    const [openManualCloseModal, setOpenManualCloseModal] = useState(false);
    useServerErrorEffect(setEntryManualCloseRequestState, () =>
        setOpenManualCloseModal(false),
    );
    useServerSuccessEffect(setEntryManualCloseRequestState, () => {
        refetchCloudConnectorPeriphery();
        setOpenManualCloseModal(false);
    });
    const [
        setBarrierOpensWithoutValdiationState,
        setBarrierOpensWithoutValdiation,
    ] = useServerWrite<BarrierOpensWithoutValidationRequest, null>(
        ({ operatorId, zoneId, onPremiseId }) => ({
            url: `/ui-api/operator-account/${operatorId}/zone-barrier-gate/${zoneId}/set-exit-opens-without-validation/${onPremiseId}`,
        }),
    );
    const [
        openBarrierWithoutValdiationModal,
        setOpenBarrierWithoutValdiationModal,
    ] = useState(false);
    const barrierOpensWithoutValidation = peripheryConfig.events.some(
        event =>
            event.eventType ===
            CloudConnectorPeripheryEventType.BARRIER_OPENS_WITHOUT_VALIDATION,
    );
    useServerErrorEffect(setBarrierOpensWithoutValdiationState, () =>
        setOpenBarrierWithoutValdiationModal(false),
    );
    useServerSuccessEffect(setBarrierOpensWithoutValdiationState, () => {
        refetchCloudConnectorPeriphery();
        setOpenBarrierWithoutValdiationModal(false);
    });

    const isRequestPending =
        peripheryCommandRequestState.status === RequestStatus.PENDING ||
        setEntryManualCloseRequestState.status === RequestStatus.PENDING ||
        setBarrierOpensWithoutValdiationState.status === RequestStatus.PENDING;

    return (
        <Stack
            direction={'column'}
            sx={{
                padding: theme => theme.spacing(3),
            }}
        >
            <RequestErrorAlert
                open={
                    peripheryCommandRequestState.status ===
                        RequestStatus.ERROR ||
                    setEntryManualCloseRequestState.status ===
                        RequestStatus.ERROR
                }
            />
            <PeripheryEventInfos events={peripheryConfig.events} />

            <Box
                sx={{
                    textTransform: 'uppercase',
                    paddingTop: theme => theme.spacing(2),
                }}
            >
                <PeripheryInfo peripheryConfig={peripheryConfig} />
            </Box>

            {!peripheryConfig.withoutPresence && (
                <Box sx={{ paddingTop: theme => theme.spacing(2) }}>
                    <InputLabel
                        sx={{
                            textTransform: 'uppercase',
                        }}
                    >
                        <Localized
                            de="Barriere"
                            fr="Barrière"
                            it="Barriera"
                            en="Barrier"
                        />
                    </InputLabel>
                    {peripheryConfig.peripheryType ===
                        CloudConnectorPeripheryType.EXIT && (
                        <Box>
                            <FormControlLabel
                                label={
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            color: theme =>
                                                theme.palette.primary.main,
                                            ...Typo.caption,
                                        }}
                                    >
                                        <Localized
                                            de="öffnen ohne Validierung"
                                            fr="ouvrir sans vérification"
                                            it="aprire senza convalida"
                                            en="open without validation"
                                        />
                                        <Tooltip
                                            title={
                                                <>
                                                    <Box>
                                                        <Localized
                                                            de="Wenn diese Option aktiviert ist und der Benutzer ein gültiges Identifikationsmedium (Ticket, usw.) vorweist, öffnet sich die Barriere, ohne dass die Transaktion validiert wird."
                                                            fr="Si cette option est activée et l'utilisateur présente un moyen d'identification valide (ticket, etc.), la barrière s'ouvre sans que la transaction soit validée."
                                                            it="Se questa opzione è attiva e l'utente presenta un mezzo d'identificazione valido (ticket, ecc.), la barriera si apre senza che la transazione venga convalidata."
                                                            en="If this option is enabled and the user presents a valid identification medium (ticket, etc.), the barrier will open without the transaction being validated."
                                                        />
                                                    </Box>
                                                    <Box>
                                                        <Localized
                                                            de="Die Ausfahrt wird gegebenenfalls unter den ungewöhnlichen Bewegungen erfasst."
                                                            fr="La sortie, le cas échéant, est enregistrée dans les mouvements atypiques."
                                                            it="L'uscita, se del caso, viene registrata nei movimenti anomali."
                                                            en="The exit is recorded under unusual movements, if applicable."
                                                        />
                                                    </Box>
                                                </>
                                            }
                                            placement="top"
                                            enterTouchDelay={0}
                                        >
                                            <InfoIcon
                                                onClick={e => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                }}
                                                sx={{
                                                    color: theme =>
                                                        theme.palette.blue.main,
                                                    marginLeft: '4px',
                                                    fontSize: '18px',
                                                }}
                                            />
                                        </Tooltip>
                                    </Box>
                                }
                                control={
                                    <Switch
                                        checked={barrierOpensWithoutValidation}
                                        disabled={
                                            isRequestPending || isDisconnected
                                        }
                                        onChange={(_e, checked) => {
                                            if (!checked) {
                                                setBarrierOpensWithoutValdiation(
                                                    {
                                                        operatorId:
                                                            currentLogin.mandantId,
                                                        zoneId: peripheryConfig.zoneId,
                                                        onPremiseId:
                                                            peripheryConfig.onPremiseId,
                                                        openWithoutValidation:
                                                            checked,
                                                    },
                                                );
                                            } else {
                                                setOpenBarrierWithoutValdiationModal(
                                                    true,
                                                );
                                            }
                                        }}
                                    />
                                }
                            />
                        </Box>
                    )}
                    <Box>
                        <FormControlLabel
                            label={
                                <Box
                                    sx={{
                                        color: theme =>
                                            theme.palette.primary.main,
                                        ...Typo.caption,
                                    }}
                                >
                                    <Localized
                                        de="dauerhaft offen"
                                        fr="ouverte en permanence"
                                        it="permanentemente aperta"
                                        en="permanently open"
                                    />
                                </Box>
                            }
                            control={
                                <Switch
                                    checked={barrierPermanentlyOpen}
                                    disabled={
                                        peripheryConfig.manualClose == true ||
                                        isRequestPending ||
                                        isDisconnected
                                    }
                                    onChange={(_e, checked) => {
                                        if (!checked) {
                                            executePeripheryCommand({
                                                operatorId:
                                                    currentLogin.mandantId,
                                                zoneId: peripheryConfig.zoneId,
                                                peripheryType:
                                                    peripheryConfig.peripheryType,
                                                onPremiseId:
                                                    peripheryConfig.onPremiseId,
                                                commandType:
                                                    CloudConnectorCommandType.BARRIER_PERMANENTLY_OPEN,
                                                secondaryGate: null,
                                                permanentlyOpen: false,
                                                forceOutOfOrder: null,
                                                zoneBarrierGateEntryWithoutExitId:
                                                    null,
                                            });
                                        } else {
                                            setOpenPermanentlyOpenModal(true);
                                        }
                                    }}
                                />
                            }
                        />
                    </Box>
                    {peripheryConfig.peripheryType ===
                        CloudConnectorPeripheryType.ENTRY && (
                        <Box>
                            <FormControlLabel
                                label={
                                    <Box
                                        sx={{
                                            color: theme =>
                                                theme.palette.primary.main,
                                            ...Typo.caption,
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Localized
                                            de="manuell geschlossen"
                                            fr="fermée manuellement"
                                            it="chiusa manualmente"
                                            en="manually closed"
                                        />
                                        <Tooltip
                                            title={
                                                <Localized
                                                    de="gilt nur für Kurzparker"
                                                    fr="valable uniquement pour les utilisateurs publics horaires"
                                                    it="vale solo per utenti orari pubblici"
                                                    en="only valid for public hourly users"
                                                />
                                            }
                                            placement="top"
                                            enterTouchDelay={0}
                                        >
                                            <InfoIcon
                                                onClick={e => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                }}
                                                sx={{
                                                    color: theme =>
                                                        theme.palette.blue.main,
                                                    marginLeft: '4px',
                                                    fontSize: '18px',
                                                }}
                                            />
                                        </Tooltip>
                                    </Box>
                                }
                                control={
                                    <Switch
                                        checked={
                                            peripheryConfig.manualClose == true
                                        }
                                        disabled={
                                            barrierPermanentlyOpen ||
                                            isRequestPending ||
                                            isDisconnected
                                        }
                                        onChange={(_e, checked) => {
                                            if (!checked) {
                                                setEntryManualCloseRequest({
                                                    operatorId:
                                                        currentLogin.mandantId,
                                                    zoneId: peripheryConfig.zoneId,
                                                    onPremiseId:
                                                        peripheryConfig.onPremiseId,
                                                    manualClose: checked,
                                                });
                                            } else {
                                                setOpenManualCloseModal(true);
                                            }
                                        }}
                                    />
                                }
                            />
                        </Box>
                    )}
                </Box>
            )}
            <ConfirmPermanentlyOpenBarrier
                open={openPermanentlyOpenModal}
                onConfirm={() =>
                    executePeripheryCommand({
                        operatorId: currentLogin.mandantId,
                        zoneId: peripheryConfig.zoneId,
                        peripheryType: peripheryConfig.peripheryType,
                        onPremiseId: peripheryConfig.onPremiseId,
                        commandType:
                            CloudConnectorCommandType.BARRIER_PERMANENTLY_OPEN,
                        secondaryGate: null,
                        permanentlyOpen: true,
                        forceOutOfOrder: null,
                        zoneBarrierGateEntryWithoutExitId: null,
                    })
                }
                onCancel={() => setOpenPermanentlyOpenModal(false)}
                isExit={
                    peripheryConfig.peripheryType ===
                    CloudConnectorPeripheryType.EXIT
                }
            />
            <ConfirmManualCloseBarrier
                open={openManualCloseModal}
                onConfirm={() =>
                    setEntryManualCloseRequest({
                        operatorId: currentLogin.mandantId,
                        zoneId: peripheryConfig.zoneId,
                        onPremiseId: peripheryConfig.onPremiseId,
                        manualClose: true,
                    })
                }
                onCancel={() => setOpenManualCloseModal(false)}
            />
            <ConfirmBarrierOpensWithoutValidation
                open={openBarrierWithoutValdiationModal}
                onConfirm={() =>
                    setBarrierOpensWithoutValdiation({
                        operatorId: currentLogin.mandantId,
                        zoneId: peripheryConfig.zoneId,
                        onPremiseId: peripheryConfig.onPremiseId,
                        openWithoutValidation: true,
                    })
                }
                onCancel={() => setOpenBarrierWithoutValdiationModal(false)}
            />
        </Stack>
    );
}

export function ConfirmPermanentlyOpenBarrier({
    open,
    onConfirm,
    onCancel,
    isExit,
}: {
    open: boolean;
    onConfirm: () => void;
    onCancel?: () => void;
    isExit: boolean;
}) {
    return (
        <ConfirmationDialog
            open={open}
            title={
                <Localized
                    de="Dauerhafte Öffnung"
                    fr="Ouverture permanente"
                    it="Apertura permanente"
                    en="Permanently open"
                />
            }
            onCancel={onCancel}
            cancelLabel={
                <Localized
                    de="Abbrechen"
                    fr="Annuler"
                    it="Annulla"
                    en="Cancel"
                />
            }
            onConfirm={onConfirm}
            confirmLabel={
                <Localized de="Öffnen" fr="Ouvrir" it="Apri" en="Open" />
            }
            dialogVersion={ConfirmationDialogVersions.SOFT_WARNING}
        >
            <>
                {isExit && (
                    <p>
                        <Localized
                            de="Bei allen Transaktionen, die vor und während der dauerhaften Öffnung begonnen haben,"
                            fr="Pour toutes les transactions commencées avant et pendant l'ouverture permanente,"
                            it="Per tutte le transazioni iniziate prima e durante l'apertura permanente,"
                            en="For all transactions started before and during permanent opening,"
                        />
                        <b>
                            <Localized
                                de=" wird die Antipassback-Validierung, falls nötig, automatisch deaktiviert."
                                fr=" la validation antipassback est automatiquement désactivée si nécessaire."
                                it=" la convalida antipassback viene disattivata automaticamente, se necessario."
                                en=" anti-passback validation will be automatically disabled if necessary."
                            />
                        </b>
                    </p>
                )}
                <p>
                    <Localized
                        de="Möchten Sie die Barriere wirklich dauerhaft öffnen?"
                        fr="Voulez-vous vraiment ouvrir la barrière de façon permanente?"
                        it="Vuole veramente aprire in modo permanente la barriera?"
                        en="Do you really want to open the barrier permanently?"
                    />
                </p>
            </>
        </ConfirmationDialog>
    );
}

function ConfirmManualCloseBarrier({
    open,
    onConfirm,
    onCancel,
}: {
    open: boolean;
    onConfirm: () => void;
    onCancel?: () => void;
}) {
    return (
        <ConfirmationDialog
            open={open}
            title={
                <Localized
                    de="Einfahrt sperren"
                    fr="Bloquer l'entrée"
                    it="Bloccare entrata"
                    en="Lock entry"
                />
            }
            onCancel={onCancel}
            cancelLabel={
                <Localized
                    de="Abbrechen"
                    fr="Annuler"
                    it="Annulla"
                    en="Cancel"
                />
            }
            onConfirm={onConfirm}
            confirmLabel={
                <Localized de="Sperren" fr="Bloquer" it="Blocca" en="Lock" />
            }
            dialogVersion={ConfirmationDialogVersions.SOFT_WARNING}
        >
            <p>
                <Localized
                    de="Möchten Sie die Einfahrt wirklich manuell sperren?"
                    fr="Voulez-vous vraiment bloquer manuellement l'entrée?"
                    it="Vuole davvero bloccare manualmente l'entrata?"
                    en="Do you really want to manually lock the entry?"
                />
            </p>
        </ConfirmationDialog>
    );
}

function ConfirmBarrierOpensWithoutValidation({
    open,
    onConfirm,
    onCancel,
}: {
    open: boolean;
    onConfirm: () => void;
    onCancel?: () => void;
}) {
    return (
        <ConfirmationDialog
            open={open}
            title={
                <Localized
                    de="Validierung deaktivieren"
                    fr="Désactiver la validation"
                    it="Disattiva convalida"
                    en="Disable validation"
                />
            }
            onCancel={onCancel}
            cancelLabel={
                <Localized
                    de="Abbrechen"
                    fr="Annuler"
                    it="Annulla"
                    en="Cancel"
                />
            }
            onConfirm={onConfirm}
            confirmLabel={
                <Localized
                    de="Deaktivieren"
                    fr="Désactiver"
                    it="Disattiva"
                    en="Disable"
                />
            }
            dialogVersion={ConfirmationDialogVersions.SOFT_WARNING}
        >
            <p>
                <Localized
                    de="Möchten Sie wirklich, dass sich die Ausfahrt öffnet, ohne dass die Transaktion validiert wird?"
                    fr="Voulez-vous vraiment que la sortie s’ouvre sans une validation de la transaction?"
                    it="Vuole davvero che l’uscita si apra senza che la transazione venga convalidata?"
                    en="Do you really want the exit to open without the transaction being validated?"
                />
            </p>
        </ConfirmationDialog>
    );
}
